import { DAY_PART_SELECTIONS } from 'constants/menu';

import { keyBy } from 'lodash-es';

import { IWithPricingProps, SanityMenuObject } from '@rbi-ctg/menu';
import {
  ILoyaltyExternalIdentifiers,
  ILoyaltyRuleConditionEvaluation,
} from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { getWalmartQuarterlyOfferUnavailableMessage } from 'state/loyalty/hooks/utils/walmart';
import { isWalmartQuarterlyOfferGroup } from 'state/loyalty/hooks/utils/walmart-offer-group';
import { ICartEntryType } from 'state/loyalty/in-restaurant-redemption';
import { OffersServiceMode } from 'state/loyalty/offers/types';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineOfferListItem,
  LoyaltyEngineId,
  LoyaltyOffer,
  SanityOfferListItem,
  WithErrors,
  buildAppOfferGroup,
  isAppPersonalizedOffer,
  isAppPersonalizedOfferGroup,
  isEngineOfferGroup,
  isEnginePersonalizedOfferGroup,
  isEngineSystemwideOffer,
  isSanityOfferGroup,
} from 'state/loyalty/types';
import { IValidDayPart } from 'state/menu/main-menu/types';
import { applyFiltersForAvailability } from 'state/menu/menu-options/apply-filters-for-availability';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { replaceKeyRegex } from 'utils/language';

import { buildServiceModeUnavailableMessageGenerator } from './loyalty-service-modes';
import {
  evaluateOfferGroupVisibility,
  getOfferGroupInlineAlertMessage,
} from './offer-group-visibility';
import { buildOfferEvaluator } from './offer-list/offer-list-item-evaluator';
import { ReduceOffers } from './types';

/**
 * Deduplicates items by loyaltyEngineId while preserving all items without loyaltyEngineId.
 * This ensures that items like OfferGroups (which may not have loyaltyEngineId) are not
 * incorrectly filtered out, while still preventing duplicate offers.
 *
 * @param items - Array of items to deduplicate
 * @returns Deduplicated array maintaining original order
 */
const deduplicateByLoyaltyEngineId = (
  items: (AppPersonalizedOfferGroup | AppPersonalizedOffer | SanityOfferListItem)[]
): (AppPersonalizedOfferGroup | AppPersonalizedOffer | SanityOfferListItem)[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const loyaltyEngineId = (item as any)?.loyaltyEngineId;
    // Always keep items without loyaltyEngineId (e.g., OfferGroups)
    if (!loyaltyEngineId) {
      return true;
    }
    // For items with loyaltyEngineId, only keep if not seen before
    if (seen.has(loyaltyEngineId)) {
      return false;
    }
    seen.add(loyaltyEngineId);
    return true;
  });
};

// Backlink a grouped offer to its parent group's engine id.
const OFFER_GROUP_EXTERNAL_IDENTIFIER_TYPE = 'offerGroup';

/**
 * Returns the parent group's engine id from an offer's `offerGroup` backlink, or
 * `undefined`. Works even when the group container is absent this render (orphan
 * offer). Callers pass heterogeneous offer records, so we narrow with a guard.
 */
function getOfferGroupEngineId(offer: object | null | undefined): LoyaltyEngineId | undefined {
  const identifiers =
    offer && 'externalIdentifiers' in offer
      ? (offer as { externalIdentifiers?: unknown }).externalIdentifiers
      : undefined;

  if (!Array.isArray(identifiers)) {
    return undefined;
  }

  const identifier = (identifiers as ReadonlyArray<ILoyaltyExternalIdentifiers | null>).find(
    ei => ei?.type === OFFER_GROUP_EXTERNAL_IDENTIFIER_TYPE
  );
  return (identifier?.value ?? undefined) as LoyaltyEngineId | undefined;
}

/**
 * Classify the offers that arrived from the CMS between two groups: those available to the current user and those that have already been applied to the cart.
 * Filter the offers that are not available and those that do not match the base offer models.
 */
export const reduceOffers: ReduceOffers = ({
  sanityOfferListItems,
  appPersonalizedOffers = [],
  appPersonalizedOfferGroups = [],
  engineOfferListItems,
  appliedOffers,
  appliedOfferIds,
  inRestaurantRedemptionCart,
  checkLoyaltyOfferAvailability,
  getIncentiveUnavailableMessage,
  enableSortOffersForServiceMode,
  setOffersFeedbackMap,
  appliedFilters,
  selectedServiceMode,
  enableOfferGroups,
  showUnavailableWalmartQuarterlySourceGroup,
}) => {
  const hasAnyOfferListItems =
    sanityOfferListItems.length ||
    appPersonalizedOffers.length ||
    appPersonalizedOfferGroups.length;

  if (
    !hasAnyOfferListItems ||
    (!engineOfferListItems.length &&
      !appPersonalizedOffers.length &&
      !appPersonalizedOfferGroups.length)
  ) {
    return [[], []];
  }

  const appliedOfferIdSet =
    appliedOfferIds || new Set(appliedOffers.map(applied => applied.id || ''));

  function isAppliedOffer(offer: AppOffer) {
    return appliedOfferIdSet.has(offer.loyaltyEngineId);
  }

  // Create a map that includes standalone offers, offer groups, and offers inside offer groups
  const engineOfferListItemsMap: Record<LoyaltyEngineId, EngineOfferListItem> = {};
  engineOfferListItems.forEach(item => {
    // Add the item itself (standalone offer or offer group)
    engineOfferListItemsMap[item.id] = item;

    // If it's an offer group, also add each offer inside it to the map
    if (isEngineOfferGroup(item)) {
      item.offers.forEach(offer => {
        engineOfferListItemsMap[offer.id] = offer;
      });
    }
  });

  const inRestaurantRedemptionMap = keyBy(inRestaurantRedemptionCart, cartEntry =>
    cartEntry.type === ICartEntryType.OFFER ? cartEntry.details.offer._id : cartEntry.referenceId
  );
  function isInRestaurantRedemption(offer: AppOffer) {
    return Boolean(inRestaurantRedemptionMap[offer.cmsId]);
  }

  const generateServiceModeUnavailableMessage = buildServiceModeUnavailableMessageGenerator(
    selectedServiceMode
  );

  function getEngineSystemwideOfferByLoyaltyEngineId(id: LoyaltyEngineId) {
    const engineOfferListItem = engineOfferListItemsMap[id];
    if (isEngineSystemwideOffer(engineOfferListItem)) {
      return engineOfferListItem as any;
    }
    return undefined as any;
  }

  const evaluateOffer = buildOfferEvaluator({
    isAppliedOffer,
    isInRestaurantRedemption,
    getEngineSystemwideOfferByLoyaltyEngineId,
    getIncentiveUnavailableMessage,
    checkLoyaltyOfferAvailability,
    appliedFilters,
    generateServiceModeUnavailableMessage,
    enableSortOffersForServiceMode,
  });

  const topOfferListItems: AppOfferListItem[] = [];
  const centerOfferListItems: AppOfferListItem[] = [];
  const bottomOfferListItems: AppOfferListItem[] = [];
  const offersInCart: AppOffer[] = [];
  const offerIdsInCart = new Set<LoyaltyEngineId>();
  const offersFeedback = new Map<LoyaltyEngineId, ILoyaltyRuleConditionEvaluation[]>();
  const skipReasons = new Map<string, AppOffer[]>();

  const addOfferToCart = (offer: AppOffer) => {
    if (offerIdsInCart.has(offer.loyaltyEngineId)) {
      return;
    }

    offersInCart.push(offer);
    offerIdsInCart.add(offer.loyaltyEngineId);
  };

  // Build a combined list of items to process:
  // - Personalized Offer Groups first (if present)
  // - Standalone personalized offers next
  // - CMS items last
  // Dedupe offers by loyaltyEngineId, but preserve all items without loyaltyEngineId (like OfferGroups)
  const combinedOfferListItems = deduplicateByLoyaltyEngineId([
    ...appPersonalizedOfferGroups,
    ...appPersonalizedOffers,
    ...sanityOfferListItems,
  ]);

  // The engine response is authoritative for instance presence. An assigned POG can fail CMS
  // resolution and therefore never become an AppPersonalizedOfferGroup; using app groups here
  // would falsely present its source group as already exhausted.
  const enginePersonalizedOfferGroups = engineOfferListItems.filter(isEnginePersonalizedOfferGroup);
  const personalizedGroupSourceCmsIds = new Set(
    enginePersonalizedOfferGroups.map(group => group.cmsId).filter(Boolean)
  );
  const personalizedGroupSourceEngineIds = new Set(
    enginePersonalizedOfferGroups.map(group => group.baseLoyaltyEngineId).filter(Boolean)
  );

  // Pass 1: a group can be enabled yet still hide (e.g. a `canOnlyChooseOne` group
  // once one of its offers is in the cart), so standalone suppression below keys
  // off whether the container actually renders, not mere presence in the inputs.
  const visibleOfferGroupEngineIds = new Set<LoyaltyEngineId>();
  const visibleGroupContainers = new Map<
    AppPersonalizedOfferGroup | AppPersonalizedOffer | SanityOfferListItem,
    AppOfferListItem
  >();

  combinedOfferListItems.forEach(item => {
    if (isAppPersonalizedOfferGroup(item)) {
      if (!enableOfferGroups || !item?.offers?.length) {
        return;
      }

      let hasOfferInCart = false;
      const visibleOffersInGroup = item.offers
        .map(offer => {
          const res = evaluateOffer(offer);
          if (res.skipReasons.length > 0) {
            res.skipReasons.forEach(({ reason }) => {
              const next = skipReasons.get(reason) || [];
              skipReasons.set(reason, next.concat(res.appOffer as AppOffer));
            });
            return null;
          }

          if (res.offerFeedback.size > 0) {
            res.offerFeedback.forEach((value, key) => {
              offersFeedback.set(key, value);
            });
          }

          if (res.destination === 'CART') {
            hasOfferInCart = true;
            if (isAppPersonalizedOffer(res.appOffer)) {
              addOfferToCart(res.appOffer);
            }
          }

          // Ensure we keep only personalized offers within personalized offer groups.
          return isAppPersonalizedOffer(res.appOffer) ? res.appOffer : null;
        })
        .filter((offer): offer is AppPersonalizedOffer => !!offer);

      if (!visibleOffersInGroup.length) {
        return;
      }

      const visibilityEvaluation = evaluateOfferGroupVisibility({
        offerGroup: item,
        visibleOffersInGroup,
        hasOfferInCart,
      });

      if (visibilityEvaluation.visible) {
        visibleGroupContainers.set(item, {
          ...item,
          inlineAlertMessage: getOfferGroupInlineAlertMessage(item, visibleOffersInGroup),
          offers: visibleOffersInGroup,
        });
        if (item.id) {
          visibleOfferGroupEngineIds.add(item.id);
        }
        if (item.baseLoyaltyEngineId) {
          visibleOfferGroupEngineIds.add(item.baseLoyaltyEngineId);
        }
      }

      return;
    }

    if (isSanityOfferGroup(item)) {
      if (!enableOfferGroups || !item?.offers?.length) {
        return;
      }

      if (isWalmartQuarterlyOfferGroup(item)) {
        const hasPersonalizedInstance =
          personalizedGroupSourceCmsIds.has(item._id) ||
          personalizedGroupSourceEngineIds.has(item.loyaltyEngineId);

        if (hasPersonalizedInstance || !showUnavailableWalmartQuarterlySourceGroup) {
          return;
        }
      }

      const engineOfferGroup = engineOfferListItemsMap[item.loyaltyEngineId];
      if (!isEngineOfferGroup(engineOfferGroup)) {
        return;
      }

      let hasOfferInCart = false;
      // For groups, run the chain for each child offer as well
      const visibleOffersInGroup = item.offers
        .map(offer => {
          const res = evaluateOffer(offer);
          if (res.skipReasons.length > 0) {
            res.skipReasons.forEach(({ reason }) => {
              const next = skipReasons.get(reason) || [];
              skipReasons.set(reason, next.concat(res.appOffer as AppOffer));
            });
            return null;
          }

          // Merge offerFeedback if it has entries
          if (res.offerFeedback.size > 0) {
            res.offerFeedback.forEach((value, key) => {
              offersFeedback.set(key, value);
            });
          }

          if (res.destination === 'CART') {
            hasOfferInCart = true;
            addOfferToCart(res.appOffer as AppOffer);
          }
          return res.appOffer;
        })
        .filter((offer): offer is AppOffer => !!offer);

      const visibilityEvaluation = evaluateOfferGroupVisibility({
        offerGroup: item as any,
        visibleOffersInGroup,
        hasOfferInCart,
      });

      if (visibilityEvaluation.visible) {
        const appOfferGroup = buildAppOfferGroup(item, engineOfferGroup, visibleOffersInGroup);
        appOfferGroup.inlineAlertMessage = getOfferGroupInlineAlertMessage(
          appOfferGroup,
          visibleOffersInGroup
        );
        if (isWalmartQuarterlyOfferGroup(item) && showUnavailableWalmartQuarterlySourceGroup) {
          appOfferGroup.inlineAlertMessage = getWalmartQuarterlyOfferUnavailableMessage();
        }
        visibleGroupContainers.set(item, appOfferGroup);
        if (item.loyaltyEngineId) {
          visibleOfferGroupEngineIds.add(item.loyaltyEngineId);
        }
      }
    }
  });

  // Pass 2: suppress a standalone offer whose parent group did not render this
  // pass, so a not-shown group's members don't leak as standalone rows. Applied /
  // in-cart offers (destination CART) are exempt — they must still render.
  combinedOfferListItems.forEach(item => {
    if (isAppPersonalizedOfferGroup(item)) {
      const container = visibleGroupContainers.get(item);
      if (container) {
        topOfferListItems.push(container);
      }
      return;
    }

    if (isSanityOfferGroup(item)) {
      const container = visibleGroupContainers.get(item);
      if (container) {
        centerOfferListItems.push(container);
      }
      return;
    }

    const res = evaluateOffer(item);
    if (res.skipReasons.length > 0) {
      res.skipReasons.forEach(({ reason }) => {
        const next = skipReasons.get(reason) || [];
        skipReasons.set(reason, next.concat(res.appOffer as AppOffer));
      });
      return;
    }

    // Exempt applied / in-cart offers (destination CART) from suppression: their
    // parent group can legitimately hide once an offer is applied (e.g. a
    // `canOnlyChooseOne` group), which would otherwise drop the very offer the
    // user just applied.
    if (enableOfferGroups && res.destination !== 'CART') {
      const parentGroupEngineId =
        getOfferGroupEngineId(engineOfferListItemsMap[item.loyaltyEngineId]) ??
        getOfferGroupEngineId(item);
      if (parentGroupEngineId && !visibleOfferGroupEngineIds.has(parentGroupEngineId)) {
        return;
      }
    }

    // Merge offerFeedback if it has entries
    if (res.offerFeedback.size > 0) {
      res.offerFeedback.forEach((value, key) => {
        offersFeedback.set(key, value);
      });
    }

    if (res.destination === 'TOP') {
      topOfferListItems.push(res.appOffer as AppOffer);
    } else if (res.destination === 'BOTTOM') {
      bottomOfferListItems.push(res.appOffer as AppOffer);
    } else if (res.destination === 'CENTER') {
      centerOfferListItems.push(res.appOffer as AppOffer);
    } else if (res.destination === 'CART') {
      addOfferToCart(res.appOffer as AppOffer);
    }
  });

  const offerListItems = [...topOfferListItems, ...centerOfferListItems, ...bottomOfferListItems];

  const offersFeedbackObject = Object.fromEntries(offersFeedback);
  setOffersFeedbackMap(offersFeedbackObject);

  return [offerListItems, offersInCart];
};

/**
 *
 * Given a string, will look for a key like {{KEY}} and will replace it with the given value.
 *
 * @param stringValue A string that might contain an specific key to be replaced
 * @param key the key you want to replaced with value
 * @param value the value you want to use to replace the KEY
 * @returns the string with the replaced key
 */
export const interpolateSanityKey = (stringValue: string, key: string, value: any) => {
  return stringValue.replace(replaceKeyRegex(key), value);
};

export const getServiceModeText = (
  // Remove the string `type`
  offerServiceMode: OffersServiceMode | string | undefined
) => {
  if (offerServiceMode === OffersServiceMode.DELIVERY_ONLY) {
    return t('deliveryOnly');
  } else if (offerServiceMode === OffersServiceMode.PICKUP_ONLY) {
    return t('pickUpOnly');
  }

  return undefined;
};

export const getOfferNextAvailableDayPart = (
  storeDayParts: ReadonlyArray<IValidDayPart>,
  offerDayParts: LoyaltyOffer['daypart'],
  options: { burgersForBreakfastEnabled?: boolean | null } = { burgersForBreakfastEnabled: false }
): IValidDayPart | undefined => {
  const incentiveDayPartsSet = new Set(offerDayParts?.map(part => part?.toLowerCase()));
  // Burgers for Breakfast
  if (
    incentiveDayPartsSet.size === 1 &&
    incentiveDayPartsSet.has(DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST.toLowerCase()) &&
    !options.burgersForBreakfastEnabled
  ) {
    return;
  }
  return storeDayParts.find(
    dayPart => dayPart.key && incentiveDayPartsSet.has(dayPart.key.toLowerCase())
  );
};

export const incentiveHasEvaluationError = (
  incentive: WithErrors,
  errorCode: IncentiveEvaluationErrorCodes
) => {
  return (
    incentive.errors && incentive.errors.find((error: { code: string }) => error.code === errorCode)
  );
};

/**
 * Util to check the store availability of the menu entities related to the given offer for
 * 1M stores. It ignores day part availability
 */
export const isOneMenuOfferEntityAvailable = (
  offer: AppOffer,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode
) => {
  const isEntityAvailable = (entityId: string) => {
    const menuEntry = indexedMenu.find(entityId);
    // No day parts params are passed to avoid filtering because of day parts unavailability
    return !!menuEntry && isEntryAvailable(menuEntry, posServiceMode);
  };

  const [, ...sanitizedId] = offer.cmsId.split('-');
  return isEntityAvailable(offer.cmsId) || isEntityAvailable(sanitizedId.join('-'));
};

/**
 * Util to check the store availability of the menu entities related to the given offer for
 * Legacy stores. It ignores day part availability
 */
export const isLegacyMenuOfferEntityAvailable = (
  offer: AppOffer,
  prices: IWithPricingProps['prices'],
  vendor: IWithPricingProps['vendor']
) => {
  if (!offer.incentives) {
    return false;
  }

  // No day parts params are passed to avoid filtering because of day parts unavailability
  const isEntityAvailable = applyFiltersForAvailability({
    prices,
    vendor,
  });

  // For store availability, we only care about Combo, Picker or Items
  const menuObjectsIncentives = offer.incentives.filter(
    menuObject => !!menuObject && ['Combo', 'Picker', 'Item'].includes(menuObject.__typename || '')
  );

  return menuObjectsIncentives.every(menuObject =>
    isEntityAvailable((menuObject as unknown) as SanityMenuObject)
  );
};

export function orderOffersBySanity<
  T extends {
    loyaltyEngineId?: string;
    configId?: string | null;
  }
>(offers: T[], orderReference: { loyaltyEngineId: string }[]): T[] {
  const orderMap = new Map(orderReference.map((ref, index) => [ref.loyaltyEngineId, index]));

  return [...offers].sort((a, b) => {
    const keyA = a.configId ?? a.loyaltyEngineId;
    const keyB = b.configId ?? b.loyaltyEngineId;

    const indexA = orderMap.get(keyA ?? '') ?? Infinity;
    const indexB = orderMap.get(keyB ?? '') ?? Infinity;

    return indexA - indexB;
  });
}
